import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  ExternalLink,
  ListPlus,
  Share2,
  ArrowUpFromLine,
  Network,
  Calendar,
  BookOpen,
  CheckCircle,
  Unlock,
  Tag,
  Sparkles,
  Clock,
  FileType,
  Shield,
  Users,
} from 'lucide-react'
import { getResource, getJournal, exploreConnections } from '../../services/api'
import { useTaxonomy } from '../../contexts/TaxonomyContext'
import type { SearchResult, JournalArticle, WebMediaResource } from '../../types'
import AddToListModal from '../../components/lists/AddToListModal'
import ShareModal from '../../components/lists/ShareModal'
import MendeleyExport from '../../components/mendeley/MendeleyExport'

function isJournalArticle(resource: SearchResult): resource is JournalArticle & { _score: number; _source_index: string } {
  return 'record_id' in resource
}

function getResourceId(resource: SearchResult): string {
  return isJournalArticle(resource) ? resource.record_id : (resource as WebMediaResource & { _score: number; _source_index: string }).resource_id
}

export default function ResourceDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { activeTaxonomy, taxonomies } = useTaxonomy()
  const [showAddToList, setShowAddToList] = useState(false)
  const [showShare, setShowShare] = useState(false)
  const [showMendeleyExport, setShowMendeleyExport] = useState(false)

  const { data: resource, isLoading, error } = useQuery({
    queryKey: ['resource', id],
    queryFn: async () => {
      try {
        return await getResource(id!)
      } catch {
        return await getJournal(id!)
      }
    },
    enabled: !!id,
  })

  const { data: relatedData } = useQuery({
    queryKey: ['connections', id],
    queryFn: () => exploreConnections(id!),
    enabled: !!id,
  })

  if (isLoading) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="animate-pulse">
          <div className="h-3 bg-gray-200 rounded w-20 mb-4" />
          <div className="h-8 bg-gray-200 rounded w-3/4 mb-4" />
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-6" />
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 rounded w-full" />
              <div className="h-4 bg-gray-200 rounded w-full" />
              <div className="h-4 bg-gray-200 rounded w-3/4" />
              <div className="h-4 bg-gray-200 rounded w-5/6" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !resource) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="text-center py-16">
          <BookOpen className="mx-auto h-12 w-12 text-gray-300" />
          <h2 className="mt-3 text-lg font-medium text-gray-900">Resource Not Found</h2>
          <p className="text-gray-500 mt-1">The requested resource could not be loaded.</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 text-sm font-medium text-acad-600 hover:text-acad-700"
          >
            &larr; Go Back
          </button>
        </div>
      </div>
    )
  }

  const isJournal = isJournalArticle(resource)
  const resourceId = getResourceId(resource)
  const resourceType = isJournal ? 'journal' : (resource as any).resource_type || 'document'
  const journal = isJournal ? resource as JournalArticle : null
  const webResource = !isJournal ? resource as unknown as WebMediaResource : null
  const sourceUrl = journal?.source_url || journal?.doi ? `https://doi.org/${journal.doi}` : webResource?.source_url
  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Back navigation */}
      <button
        onClick={() => navigate(-1)}
        className="text-acad-600 hover:text-acad-700 mb-4 flex items-center gap-1 text-sm font-medium"
      >
        &larr; Back
      </button>

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-flex items-center rounded-full bg-acad-50 px-2.5 py-0.5 text-xs font-medium text-acad-600 capitalize">
            {resourceType}
          </span>
          {journal?.peer_reviewed && (
            <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-0.5 text-xs font-medium text-green-700">
              <CheckCircle className="h-3 w-3" />
              Peer Reviewed
            </span>
          )}
          {journal?.open_access && (
            <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700">
              <Unlock className="h-3 w-3" />
              Open Access
            </span>
          )}
          {webResource?.peer_reviewed && (
            <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-0.5 text-xs font-medium text-green-700">
              <CheckCircle className="h-3 w-3" />
              Peer Reviewed
            </span>
          )}
        </div>
        <h1 className="text-2xl font-bold text-gray-900">{resource.title}</h1>

        {/* Authors / Creator */}
        {journal?.authors && journal.authors.length > 0 && (
          <p className="text-gray-600 mt-2">{journal.authors.join(', ')}</p>
        )}
        {webResource?.creator && (
          <p className="text-gray-600 mt-2">{webResource.creator}</p>
        )}

        {/* Meta info line */}
        <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-gray-500">
          {journal?.journal_name && (
            <span className="flex items-center gap-1">
              <BookOpen className="h-3.5 w-3.5" />
              {journal.journal_name}
            </span>
          )}
          {(journal?.publication_date || webResource?.publication_date) && (
            <span className="flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" />
              {new Date(journal?.publication_date || webResource?.publication_date || '').toLocaleDateString()}
            </span>
          )}
          {journal && journal.citation_count > 0 && (
            <span>Cited {journal.citation_count} times</span>
          )}
          {webResource?.duration && (
            <span className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              {webResource.duration}
            </span>
          )}
          {webResource?.license_type && (
            <span className="flex items-center gap-1">
              <Shield className="h-3.5 w-3.5" />
              {webResource.license_type}
            </span>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <button
          onClick={() => setShowAddToList(true)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <ListPlus className="h-4 w-4" />
          Add to List
        </button>
        <button
          onClick={() => setShowShare(true)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <Share2 className="h-4 w-4" />
          Share
        </button>
        <button
          onClick={() => setShowMendeleyExport(true)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <ArrowUpFromLine className="h-4 w-4" />
          Export to Mendeley
        </button>
        {sourceUrl && (
          <a
            href={sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-lg bg-acad-500 px-3 py-2 text-sm font-medium text-white hover:bg-acad-600 transition-colors"
          >
            <ExternalLink className="h-4 w-4" />
            Open Source
          </a>
        )}
        <button
          onClick={() => navigate(`/connections?start=${resourceId}`)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-acad-300 px-3 py-2 text-sm font-medium text-acad-600 hover:bg-acad-50 transition-colors"
        >
          <Network className="h-4 w-4" />
          Explore Connections
        </button>
      </div>

      {/* Content Sections */}
      <div className="space-y-6">
        {/* Abstract / Description */}
        {(journal?.abstract || webResource?.description) && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-3">
              {isJournal ? 'Abstract' : 'Description'}
            </h2>
            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
              {journal?.abstract || webResource?.description}
            </p>
          </div>
        )}

        {/* TLDR Summary (journals) */}
        {journal?.tldr && (
          <div className="bg-acad-50 rounded-xl border border-acad-200 p-6">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-4 w-4 text-acad-600" />
              <h2 className="text-base font-semibold text-acad-800">TLDR Summary</h2>
            </div>
            <p className="text-sm text-acad-900">{journal.tldr}</p>
          </div>
        )}

        {/* Thumbnail for web resources */}
        {webResource?.thumbnail_url && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <img
              src={webResource.thumbnail_url}
              alt={resource.title}
              className="rounded-lg max-h-64 object-cover"
            />
          </div>
        )}

        {/* Web resource extra metadata */}
        {webResource && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-3">Resource Details</h2>
            <dl className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <dt className="font-medium text-gray-500">Type</dt>
                <dd className="mt-0.5 text-gray-900 capitalize flex items-center gap-1.5">
                  <FileType className="h-3.5 w-3.5 text-gray-400" />
                  {webResource.resource_type}
                </dd>
              </div>
              {webResource.publisher && (
                <div>
                  <dt className="font-medium text-gray-500">Publisher</dt>
                  <dd className="mt-0.5 text-gray-900">{webResource.publisher}</dd>
                </div>
              )}
              {webResource.license_type && (
                <div>
                  <dt className="font-medium text-gray-500">License</dt>
                  <dd className="mt-0.5 text-gray-900">{webResource.license_type}</dd>
                </div>
              )}
              {webResource.duration && (
                <div>
                  <dt className="font-medium text-gray-500">Duration</dt>
                  <dd className="mt-0.5 text-gray-900">{webResource.duration}</dd>
                </div>
              )}
              <div>
                <dt className="font-medium text-gray-500">Source</dt>
                <dd className="mt-0.5">
                  <a
                    href={webResource.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-acad-600 hover:text-acad-700 flex items-center gap-1"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    View Source
                  </a>
                </dd>
              </div>
            </dl>
          </div>
        )}

        {/* DOI link for journals */}
        {journal?.doi && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-3">Publication Details</h2>
            <dl className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <dt className="font-medium text-gray-500">DOI</dt>
                <dd className="mt-0.5">
                  <a
                    href={`https://doi.org/${journal.doi}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-acad-600 hover:text-acad-700 flex items-center gap-1"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    {journal.doi}
                  </a>
                </dd>
              </div>
              {journal.publisher && (
                <div>
                  <dt className="font-medium text-gray-500">Publisher</dt>
                  <dd className="mt-0.5 text-gray-900">{journal.publisher}</dd>
                </div>
              )}
              {journal.journal_name && (
                <div>
                  <dt className="font-medium text-gray-500">Journal</dt>
                  <dd className="mt-0.5 text-gray-900">{journal.journal_name}</dd>
                </div>
              )}
              <div>
                <dt className="font-medium text-gray-500">Citation Count</dt>
                <dd className="mt-0.5 text-gray-900">{journal.citation_count}</dd>
              </div>
            </dl>
          </div>
        )}

        {/* Topics */}
        {resource.topics && resource.topics.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-3">
              <Tag className="h-4 w-4 text-acad-600" />
              <h2 className="text-base font-semibold text-gray-900">Topics</h2>
            </div>
            <div className="flex flex-wrap gap-2">
              {resource.topics.map((topic, i) => (
                <span
                  key={i}
                  className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700"
                >
                  {topic}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Audience Groups */}
        {resource.audience_groups && resource.audience_groups.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-3">
              <Users className="h-4 w-4 text-acad-600" />
              <h2 className="text-base font-semibold text-gray-900">Audience Groups</h2>
            </div>
            <div className="flex flex-wrap gap-2">
              {resource.audience_groups.map((group, i) => (
                <span
                  key={i}
                  className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700"
                >
                  {group}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Taxonomy Subjects */}
        {resource.subjects && Object.keys(resource.subjects).length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-3">
              Taxonomy Subjects
            </h2>
            <div className="space-y-4">
              {Object.entries(resource.subjects).map(([taxonomyId, subjects]) => {
                const taxName = taxonomies.find((t) => t.id === taxonomyId)?.name || taxonomyId
                const isActive = taxonomyId === activeTaxonomy
                return (
                  <div key={taxonomyId}>
                    <h3 className={`text-sm font-medium mb-2 ${isActive ? 'text-acad-700' : 'text-gray-600'}`}>
                      {taxName}
                      {isActive && (
                        <span className="ml-2 text-xs text-acad-500 font-normal">(active)</span>
                      )}
                    </h3>
                    <div className="flex flex-wrap gap-1.5">
                      {subjects.map((subject, i) => (
                        <span
                          key={i}
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            isActive
                              ? 'bg-acad-50 text-acad-700 border border-acad-200'
                              : 'bg-gray-50 text-gray-600 border border-gray-200'
                          }`}
                        >
                          {subject}
                        </span>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Cross-domain subjects */}
        {resource.cross_domain_subjects && resource.cross_domain_subjects.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="h-4 w-4 text-purple-600" />
              <h2 className="text-base font-semibold text-gray-900">Cross-Domain Subjects</h2>
              <span className="inline-flex items-center gap-1 rounded-full bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-600">
                <Sparkles className="h-3 w-3" />
                AI Discovered
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {resource.cross_domain_subjects.map((subject, i) => (
                <span
                  key={i}
                  className="inline-flex items-center rounded-full bg-purple-50 px-3 py-1 text-xs font-medium text-purple-700 border border-purple-200"
                >
                  {subject}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Related Resources */}
        {relatedData && relatedData.nodes && relatedData.nodes.length > 1 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Network className="h-4 w-4 text-acad-600" />
                <h2 className="text-base font-semibold text-gray-900">Related Resources</h2>
              </div>
              <button
                onClick={() => navigate(`/connections?start=${resourceId}`)}
                className="text-xs font-medium text-acad-600 hover:text-acad-700"
              >
                View All Connections &rarr;
              </button>
            </div>
            <div className="space-y-2">
              {relatedData.nodes
                .filter((n: any) => n.id !== resourceId)
                .slice(0, 5)
                .map((node: any) => (
                  <div
                    key={node.id}
                    className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:border-acad-200 cursor-pointer transition-colors"
                    onClick={() => navigate(`/resource/${node.id}`)}
                  >
                    <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 capitalize">
                      {node.type}
                    </span>
                    <span className="text-sm text-gray-900 flex-1">{node.label}</span>
                    {node.score !== undefined && (
                      <span className="text-xs text-gray-400">
                        {(node.score * 100).toFixed(0)}% similar
                      </span>
                    )}
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showAddToList && (
        <AddToListModal
          isOpen={showAddToList}
          onClose={() => setShowAddToList(false)}
          resourceId={resourceId}
          resourceType={resourceType}
          title={resource.title}
        />
      )}

      {showShare && (
        <ShareModal
          isOpen={showShare}
          onClose={() => setShowShare(false)}
          resourceId={resourceId}
          resourceType={resourceType}
          title={resource.title}
        />
      )}

      {showMendeleyExport && (
        <MendeleyExport
          isOpen={showMendeleyExport}
          onClose={() => setShowMendeleyExport(false)}
          resourceId={resourceId}
          title={resource.title}
          resourceType={resourceType}
        />
      )}
    </div>
  )
}
